import { createFileRoute, Link } from "@tanstack/react-router";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";

const URL = "https://rajshamani.life/guides/d2c-brands-india";
const TITLE = "How to Start a D2C Brand in India (2026) — Raj Shamani";
const DESC =
  "A practical 2026 playbook for launching a D2C brand in India: sourcing, manufacturing, distribution, and scaling — with House of X as a case study.";

export const Route = createFileRoute("/guides/d2c-brands-india")({
  head: () => ({
    meta: [
      { key: "title", title: TITLE },
      { key: "description", name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "How to Start a D2C Brand in India",
          description: DESC,
          author: { "@type": "Person", name: "Raj Shamani", url: "https://rajshamani.life" },
          step: [
            { "@type": "HowToStep", name: "Validate the wedge", text: "Pick a category with strong unit economics and clear buyer pain." },
            { "@type": "HowToStep", name: "Source and manufacture", text: "Find Indian manufacturers via Indiamart, trade shows, or contract MOQs starting under ₹2L." },
            { "@type": "HowToStep", name: "Build distribution", text: "Launch on your own Shopify, then layer in Amazon, Flipkart, Blinkit, Zepto, and modern trade." },
            { "@type": "HowToStep", name: "Acquire customers", text: "Use Meta + creator marketing for top-of-funnel, WhatsApp + email for repeat." },
            { "@type": "HowToStep", name: "Scale capital-efficiently", text: "Track CM2, fund inventory via revenue-based finance before raising equity." },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: GuidePage,
});

const FAQS: { q: string; a: string }[] = [
  {
    q: "How much capital do I need to start a D2C brand in India?",
    a: "You can launch a single-SKU D2C brand in India with ₹3–10 lakh: contract manufacturing MOQs start around ₹1–3L, plus packaging, FSSAI/CDSCO registration, a Shopify store, and initial performance marketing budget.",
  },
  {
    q: "Which is the best channel for D2C brands in India — Shopify, Amazon, or quick commerce?",
    a: "Start on Shopify for margin and data ownership, then layer marketplaces (Amazon, Flipkart, Nykaa, Myntra) for discovery, and quick commerce (Blinkit, Zepto, Instamart) for repeat and impulse. In 2026, quick commerce often becomes the largest channel within six months.",
  },
  {
    q: "What is a healthy CAC and contribution margin for an Indian D2C brand?",
    a: "Aim for CM2 (revenue minus COGS, shipping, payment gateway, returns, and marketing) of at least 15% by month 12. If CM2 is negative, fix pricing or product before spending on growth.",
  },
  {
    q: "Do I need FSSAI or other licences before selling?",
    a: "Yes. Register FSSAI for food, CDSCO for cosmetics and supplements, and BIS for electronics before your first sale. Add GST and IEC if you plan to export.",
  },
  {
    q: "Should I raise funding or bootstrap my D2C brand?",
    a: "Bootstrap to ₹50L–₹1 Cr monthly revenue before raising equity. Use revenue-based financing (Klub, Velocity, GetVantage) for inventory. Raise equity to compress time, not to survive.",
  },
];

function H2({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2 id={id} className="text-2xl md:text-3xl font-display text-gold mt-12 mb-4 scroll-mt-24">
      {children}
    </h2>
  );
}

function GuidePage() {
  return (
    <article className="container max-w-3xl py-12 md:py-16">
      <PageBreadcrumb current="D2C Brands India" />

      <header className="mt-6 mb-10">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
          Guide · ~12 min read
        </p>
        <h1 className="text-4xl md:text-5xl font-display leading-tight">
          How to Start a D2C Brand in India
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">
          A founder-friendly playbook covering sourcing, distribution, marketing, and scaling — drawn from
          what's working at <strong className="text-foreground">House of X</strong>, Raj Shamani's venture
          studio behind brands like Munchies and others built for the Indian consumer.
        </p>
      </header>

      <nav aria-label="On this page" className="border border-border rounded-lg p-5 mb-12 bg-card/40">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">On this page</p>
        <ol className="space-y-1 text-sm list-decimal pl-5">
          <li><a className="hover:text-gold" href="#why-now">Why D2C in India, why now</a></li>
          <li><a className="hover:text-gold" href="#wedge">Pick the wedge</a></li>
          <li><a className="hover:text-gold" href="#sourcing">Sourcing &amp; manufacturing</a></li>
          <li><a className="hover:text-gold" href="#distribution">Distribution stack</a></li>
          <li><a className="hover:text-gold" href="#marketing">Marketing &amp; creator GTM</a></li>
          <li><a className="hover:text-gold" href="#unit-economics">Unit economics that survive</a></li>
          <li><a className="hover:text-gold" href="#scaling">Scaling &amp; capital</a></li>
          <li><a className="hover:text-gold" href="#hox">Case study: House of X</a></li>
          <li><a className="hover:text-gold" href="#mistakes">Mistakes to avoid</a></li>
        </ol>
      </nav>

      <div className="prose prose-invert max-w-none prose-p:text-muted-foreground prose-li:text-muted-foreground prose-strong:text-foreground">
        <H2 id="why-now">1. Why D2C in India, why now</H2>
        <p>
          India has crossed 800M+ internet users, UPI handles ~14B monthly transactions, and quick-commerce
          (Blinkit, Zepto, Instamart) has compressed the discovery-to-purchase loop to under 10 minutes.
          That combination — cheap reach, near-zero payment friction, and instant fulfilment — is why a new
          Indian consumer brand can go from idea to ₹1 crore monthly revenue in 12–18 months, something that
          took 5+ years in the offline-only era.
        </p>

        <H2 id="wedge">2. Pick the wedge</H2>
        <p>
          Don't start with "a brand". Start with one SKU that solves a specific, repeat-purchase problem for
          a clearly defined buyer. The questions worth answering before you spend any money:
        </p>
        <ul>
          <li><strong>Is the category growing?</strong> Check Google Trends + Blinkit/Zepto bestseller lists.</li>
          <li><strong>Is there pricing room?</strong> If the category leader sells at ₹X, you need to be either
            premium (1.5–2× X with a real reason) or value (0.7× X with structurally lower cost).</li>
          <li><strong>Will they buy again?</strong> If repeat purchase &lt; 30%, you're running ads forever.</li>
        </ul>

        <H2 id="sourcing">3. Sourcing &amp; manufacturing</H2>
        <p>
          Most first-time founders over-engineer this. Three realistic paths:
        </p>
        <ul>
          <li><strong>Contract manufacturing (CM)</strong> — fastest. MOQs in food, personal care, and
            supplements start at ₹1–3L. Find CMs on Indiamart, at trade shows (Anuga FoodTec, In-Cosmetics),
            or through industry WhatsApp groups.</li>
          <li><strong>Co-manufacturing with a brand-side R&amp;D partner</strong> — better margins,
            ~₹5–15L to start, formulation IP stays with you.</li>
          <li><strong>Own facility</strong> — only after ₹50L+ monthly revenue in a single SKU.</li>
        </ul>
        <p>
          Register FSSAI (food), CDSCO (cosmetics/supplements), BIS (electronics) before your first sale.
          GST + IEC if you ever plan to export.
        </p>

        <H2 id="distribution">4. Distribution stack</H2>
        <p>
          The Indian D2C distribution ladder in 2026 looks like this — climb it in order, not all at once:
        </p>
        <ol>
          <li><strong>Your own Shopify</strong> — full margin, full data. Aim for 20–30% of revenue here long-term.</li>
          <li><strong>Marketplaces (Amazon, Flipkart, Myntra, Nykaa)</strong> — discovery + trust, 18–30% take rate.</li>
          <li><strong>Quick commerce (Blinkit, Zepto, Instamart, BigBasket)</strong> — repeat + impulse, 25–35% take rate but enormous AOV velocity.</li>
          <li><strong>Modern trade + general trade</strong> — only once velocity is proven online. Distributor margins eat 35–45% combined.</li>
        </ol>

        <H2 id="marketing">5. Marketing &amp; creator GTM</H2>
        <p>
          Performance ads alone will not build an Indian D2C brand in 2026 — CACs on Meta have roughly
          tripled since 2021. The mix that's working:
        </p>
        <ul>
          <li><strong>Top of funnel:</strong> Meta + Google performance for cold reach; creator-led short
            videos (Instagram Reels, YouTube Shorts) for low-cost, high-trust discovery.</li>
          <li><strong>Mid funnel:</strong> founder storytelling on LinkedIn / Instagram + podcast guest
            appearances. This is where category-defining brands separate from commodity brands.</li>
          <li><strong>Bottom + repeat:</strong> WhatsApp flows (AiSensy, Wati), email (Klaviyo), and a
            simple loyalty program. Cheapest acquisition channel you'll ever run.</li>
        </ul>

        <H2 id="unit-economics">6. Unit economics that survive</H2>
        <p>
          The number that matters is <strong>Contribution Margin 2 (CM2)</strong>: revenue minus COGS,
          shipping, payment gateway, returns, <em>and</em> marketing. A healthy Indian D2C brand runs at
          CM2 ≥ 15% by month 12. If CM2 is negative, growth is destroying value — fix unit economics before
          spending on growth.
        </p>

        <H2 id="scaling">7. Scaling &amp; capital</H2>
        <p>
          Bootstrap to ₹50L–₹1 Cr monthly revenue before raising equity. Inventory is the single biggest
          cash drain — use revenue-based financing (Klub, Velocity, GetVantage) for working capital instead
          of diluting. When you do raise, raise to <em>compress time</em>, not to survive.
        </p>

        <H2 id="hox">8. Case study: House of X</H2>
        <p>
          House of X, the venture studio Raj Shamani co-founded, runs a portfolio model rather than a single
          brand. The thesis: shared back-end (sourcing, supply chain, performance marketing, creator network),
          independent front-ends (each brand owns its category and identity). Why founders should care:
        </p>
        <ul>
          <li><strong>Shared CAC infrastructure:</strong> one creator network amortised across multiple brands.</li>
          <li><strong>Category-specialist operators</strong> instead of one team trying to do everything.</li>
          <li><strong>Capital efficiency</strong> through shared supply-chain and ops — the same lesson applies
            to a solo founder: outsource what isn't your wedge.</li>
        </ul>

        <H2 id="mistakes">9. Mistakes to avoid</H2>
        <ul>
          <li>Launching with 8 SKUs. Launch with 1, win it, then extend.</li>
          <li>Raising before product-market fit. You'll spend the money proving the thesis was wrong.</li>
          <li>Treating quick commerce as a side channel. In 2026, it's often the largest channel within 6 months.</li>
          <li>Ignoring repeat-purchase rate. No brand has ever scaled profitably on first-time buyers.</li>
        </ul>
      </div>

      <aside className="mt-16 p-6 md:p-8 rounded-xl border border-gold/30 bg-gold/5">
        <h2 className="text-2xl font-display text-gold mb-3">Want Raj's full breakdown on D2C in India?</h2>
        <p className="text-muted-foreground mb-5">
          Listen to the Figuring Out podcast — episodes with India's top consumer founders go deep on
          everything in this guide.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/podcast"
            className="inline-flex items-center px-5 py-2.5 rounded-md bg-gold text-black font-medium hover:opacity-90"
          >
            Listen to the podcast
          </Link>
          <Link
            to="/guest"
            className="inline-flex items-center px-5 py-2.5 rounded-md border border-border hover:border-gold"
          >
            Be a guest
          </Link>
        </div>
      </aside>

      <section aria-labelledby="faq" className="mt-16">
        <h2 id="faq" className="text-2xl md:text-3xl font-display text-gold mb-6">
          Frequently asked questions
        </h2>
        <div className="divide-y divide-border border-y border-border">
          {FAQS.map((f) => (
            <details key={f.q} className="group py-4">
              <summary className="cursor-pointer font-medium text-foreground list-none flex justify-between gap-4">
                {f.q}
                <span className="text-gold transition-transform group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section aria-labelledby="related" className="mt-16">
        <h2 id="related" className="text-2xl md:text-3xl font-display text-gold mb-6">
          Keep reading
        </h2>
        <ul className="grid gap-4 sm:grid-cols-2">
          <li>
            <Link to="/guides/how-to-start-a-podcast-india" className="block p-5 rounded-lg border border-border hover:border-gold transition-colors">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Guide</p>
              <p className="font-medium">How to start a podcast in India</p>
              <p className="text-sm text-muted-foreground mt-1">Gear, distribution, guest sourcing, and monetization from the Figuring Out team.</p>
            </Link>
          </li>
          <li>
            <Link to="/ventures" className="block p-5 rounded-lg border border-border hover:border-gold transition-colors">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Ventures</p>
              <p className="font-medium">House of X &amp; Raj's D2C portfolio</p>
              <p className="text-sm text-muted-foreground mt-1">The venture studio behind Munchies and other Indian consumer brands.</p>
            </Link>
          </li>
          <li>
            <Link to="/podcast" className="block p-5 rounded-lg border border-border hover:border-gold transition-colors">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Podcast</p>
              <p className="font-medium">Figuring Out episodes with D2C founders</p>
              <p className="text-sm text-muted-foreground mt-1">Deep dives with India's top consumer entrepreneurs.</p>
            </Link>
          </li>
          <li>
            <Link to="/build-dont-talk" className="block p-5 rounded-lg border border-border hover:border-gold transition-colors">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Book</p>
              <p className="font-medium">Build, Don't Talk</p>
              <p className="text-sm text-muted-foreground mt-1">Raj Shamani's bestseller on execution over talk.</p>
            </Link>
          </li>
        </ul>
      </section>

    </article>
  );
}
